import React from 'react';
import { Helmet } from 'react-helmet-async';

const MAPBOX_SCRIPT_ID = 'mapbox_GL_JS';
const GOOGLE_MAPS_SCRIPT_ID = 'GoogleMapsApi';
export const MAP_LIBRARIES_READY_EVENT = 'localflower:map-libraries-ready';
const GOOGLE_ADS_ID = null;
// Every listed pixel gets the same events: 2090462578024463 ("LocalFlower") is
// the one linked to the ad account, 916435778090482 ("LocalFlower 2") was
// created later for the same site.
const META_PIXEL_IDS = ['2090462578024463', '916435778090482'];
const CLARITY_PROJECT_ID = 'tm12bmz7or';

const LIGHT_SEO_PAGE_EXACT_PATHS = [
  '/home-preview',
  '/blog',
  '/flower-delivery',
  '/gift-delivery',
  '/rose-delivery',
  '/flower-delivery-port-credit',
];

const LIGHT_SEO_PAGE_PATH_PREFIXES = [
  '/flower-delivery/',
  '/gift-delivery/',
  '/rose-delivery/',
  '/blog/',
  '/balloon-delivery/',
  '/cake-delivery/',
];

const isLightSeoPagePath = currentPath => {
  const path = currentPath ? currentPath.split('?')[0] : '';

  return (
    LIGHT_SEO_PAGE_EXACT_PATHS.includes(path) ||
    LIGHT_SEO_PAGE_PATH_PREFIXES.some(prefix => path.startsWith(prefix))
  );
};

/**
 * Include scripts (like Map Provider).
 * These scripts are relevant for whole application: location search in Topbar and maps on different pages.
 * However, if you don't need location search and maps, you can just omit this component from app.js
 * Note: another common point to add <scripts>, <links> and <meta> tags is Page.js
 *       and Stripe script is added in public/index.html
 *
 * Note 2: When adding new external scripts/styles/fonts/etc.,
 *         if a Content Security Policy (CSP) is turned on, the new URLs
 *         should be whitelisted in the policy. Check: server/csp.js
 */
export const IncludeScripts = props => {
  const { marketplaceRootURL: rootURL, maps, analytics } = props?.config || {};
  const { currentPath } = props;
  const { googleAnalyticsId, plausibleDomains, googleTagId } = analytics;

  const { mapProvider, googleMapsAPIKey, mapboxAccessToken } = maps || {};
  const isGoogleMapsInUse = mapProvider === 'googleMaps';
  const isMapboxInUse = mapProvider === 'mapbox';
  const includeMapLibraries = !isLightSeoPagePath(currentPath);

  // Add Google Analytics script if correct id exists (it should start with 'G-' prefix)
  // See: https://developers.google.com/analytics/devguides/collection/gtagjs
  const hasGoogleAnalyticsv4Id = googleAnalyticsId?.indexOf('G-') === 0;

  // Collect relevant map libraries
  let mapLibraries = [];
  let analyticsLibraries = [];

  // The heavy mapbox-gl renderer (~300KB) is only needed on the page that
  // actually draws a map (the search page). Everywhere else the lightweight
  // mapbox-sdk (~17KB) is enough for the topbar's address geocoding.
  const pathNoQuery = currentPath ? currentPath.split('?')[0] : '';
  const pageNeedsMapRenderer = pathNoQuery === '/s';

  if (includeMapLibraries && isMapboxInUse) {
    // NOTE: remember to update mapbox-sdk.min.js to a new version regularly.
    // mapbox-sdk.min.js is included from static folder for CSP purposes.
    // Lightweight geocoding SDK — loaded on every (non-light) page. It's only
    // used when someone types in the topbar address search (a user
    // interaction well after load), so `defer` keeps it off the critical
    // render path: it was blocking first paint by ~490ms on every page,
    // including content pages like /wishes/* that never draw a map.
    mapLibraries.push(
      <script
        key="mapboxSDK"
        src={`${rootURL}/static/scripts/mapbox/mapbox-sdk.min.js`}
        defer
      ></script>
    );

    if (pageNeedsMapRenderer) {
      // License information for v3.7.0 of the mapbox-gl-js library:
      // https://github.com/mapbox/mapbox-gl-js/blob/v3.7.0/LICENSE.txt
      mapLibraries.push(
        <link
          key="mapbox_GL_CSS"
          href="https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.css"
          rel="stylesheet"
          crossOrigin
        />
      );
      mapLibraries.push(
        <script
          id={MAPBOX_SCRIPT_ID}
          key="mapbox_GL_JS"
          src="https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.js"
          crossOrigin
        ></script>
      );
    }
  } else if (includeMapLibraries && isGoogleMapsInUse) {
    // Add Google Maps library
    mapLibraries.push(
      <script
        id={GOOGLE_MAPS_SCRIPT_ID}
        key="GoogleMapsApi"
        src={`https://maps.googleapis.com/maps/api/js?key=${googleMapsAPIKey}&libraries=places`}
        crossOrigin
      ></script>
    );
  }

  if (googleAnalyticsId && hasGoogleAnalyticsv4Id) {
    // Google Analytics: gtag.js
    // NOTE: This template is a single-page application (SPA).
    //       gtag.js sends initial page_view event after page load.
    //       but we need to handle subsequent events for in-app navigation.
    //       This is done in src/analytics/handlers.js
    //
    // The library is loaded with the *Google tag* ID. Usually that's the G-
    // measurement ID itself, but when Google has combined the GA4 stream into
    // a tag shared with Google Ads (AW-…/GT-…), only that combined ID loads —
    // gtag.js for the bare G- ID returns 404. REACT_APP_GOOGLE_TAG_ID overrides
    // the loader ID for that case; the G- ID stays the GA4 destination.
    // No crossorigin attribute, matching Google's own snippet.
    const loaderId = googleTagId || googleAnalyticsId;
    const configCalls =
      loaderId === googleAnalyticsId
        ? `gtag('config', '${googleAnalyticsId}', { cookie_flags: 'SameSite=None;Secure' });`
        : `gtag('config', '${loaderId}');
        gtag('config', '${googleAnalyticsId}', { cookie_flags: 'SameSite=None;Secure' });`;

    analyticsLibraries.push(
      <script
        key="gtag.js"
        async
        src={`https://www.googletagmanager.com/gtag/js?id=${loaderId}`}
      ></script>
    );

    // The config MUST be emitted as an inline <script> in the HTML (like the
    // Ads/Clarity tags below). A plain `if (window)` block is skipped during
    // server rendering, so the config never reached the browser and gtag.js
    // loaded but sent no hits. window.gtag is set explicitly because
    // src/analytics/handlers.js calls it on in-app navigation.
    analyticsLibraries.push(
      <script key="gtag-config">
        {`
        window.dataLayer = window.dataLayer || [];
        window.gtag = function gtag(){ dataLayer.push(arguments); };
        gtag('js', new Date());
        ${configCalls}
      `}
      </script>
    );
  }

  if (plausibleDomains) {
    // If plausibleDomains is not an empty string, include their script too.
    analyticsLibraries.push(
      <script
        key="plausible"
        defer
        src="https://plausible.io/js/script.js"
        data-domain={plausibleDomains}
        crossOrigin
      ></script>
    );
  }

  // Google Ads conversion tracking is disabled for now to reduce third-party JavaScript.
  if (GOOGLE_ADS_ID) {
    analyticsLibraries.push(
      <script
        key="gtag-ads"
        async
        src={`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`}
        crossOrigin
      ></script>
    );

    analyticsLibraries.push(
      <script key="gtag-ads-config">
        {`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${GOOGLE_ADS_ID}');
      `}
      </script>
    );
  }

  // Meta Pixel (Instagram / Facebook ads). Funnel events: util/metaPixel.js.
  if (META_PIXEL_IDS.length) {
    analyticsLibraries.push(
      <script key="fb">
        {`
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    ${META_PIXEL_IDS.map(id => `fbq('init', '${id}');`).join('\n    ')}
    fbq('track', 'PageView');
 `}
      </script>
    );

    analyticsLibraries.push(
      <noscript key="fb-img">{`<img height="1" width="1" style="display:none"
    src="https://www.facebook.com/tr?id=${META_PIXEL_IDS[0]}&ev=PageView&noscript=1"
    />`}</noscript>
    );
  }

  if (CLARITY_PROJECT_ID) {
    analyticsLibraries.push(
      <script key="clarity">
        {`
    // No session recording for private areas: the admin console and the
    // shop/customer portals are internal and full of personal data. (Guard
    // is by landing path — those areas are opened directly by URL.)
    var lfPrivate = ['admin', 'my-shop', 'account'].indexOf(window.location.pathname.split('/')[1]) !== -1;
    if (!lfPrivate) {
    (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${CLARITY_PROJECT_ID}");
    }
 `}
      </script>
    );
  }

  const isBrowser = typeof window !== 'undefined';
  const isMapboxLoaded = isBrowser && window.mapboxgl;

  // Expose the token globally so the geocoder (topbar address search) works on
  // pages that load only the lightweight SDK, not the full mapbox-gl renderer.
  if (isBrowser && isMapboxInUse) {
    window.__mapboxAccessToken = mapboxAccessToken;
  }

  // If Mapbox is loaded, we can set the accessToken already here.
  // This is the execution flow with the production build,
  // since SSR includes those map libraries to <head> of the app.
  if (isMapboxInUse && isMapboxLoaded && !window.mapboxgl.accessToken) {
    // Add access token for Mapbox library
    window.mapboxgl.accessToken = mapboxAccessToken;
  }

  // If the script is added on client side as a reaction to page navigation or
  // the app is rendered on client side entirely (e.g. HMR/WebpackDevServer),
  // we need to listen when the script is loaded.
  const onMapLibLoaded = () => {
    // At this point we know that map library is loaded after it's dynamically included
    if (isMapboxInUse && !window.mapboxgl.accessToken) {
      // Add access token for Mapbox sdk.
      window.mapboxgl.accessToken = mapboxAccessToken;
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(MAP_LIBRARIES_READY_EVENT));
    }
  };

  // React Helmet Async doesn't support onLoad prop for scripts.
  // However, it does have onChangeClientState functionality.
  // We can use that to start listen 'load' events when the library is added on client-side.
  const onChangeClientState = (newState, addedTags) => {
    if (addedTags && addedTags.scriptTags) {
      const foundScript = addedTags.scriptTags.find(s =>
        [MAPBOX_SCRIPT_ID, GOOGLE_MAPS_SCRIPT_ID].includes(s.id)
      );
      if (foundScript) {
        foundScript.addEventListener('load', onMapLibLoaded, { once: true });
      }
    }
  };

  const allScripts = [...analyticsLibraries, ...mapLibraries];
  return <Helmet onChangeClientState={onChangeClientState}>{allScripts}</Helmet>;
};
